(() => {
  if (window.__gm3ReferencePerformanceLoaded) return;
  window.__gm3ReferencePerformanceLoaded = true;

  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  const SAVE_DATA = Boolean(navigator.connection && navigator.connection.saveData);
  const scriptPromises = window.__gm3ReferenceScriptPromises || new Map();
  window.__gm3ReferenceScriptPromises = scriptPromises;

  const isVisibleLayout = (element) => Boolean(element && element.offsetParent !== null && !element.hidden);

  const hydratePoster = (video) => {
    if (!video || !video.dataset.poster) return;
    video.poster = video.dataset.poster;
    video.removeAttribute('data-poster');
  };

  const hydrateVideo = (video, shouldPlay = true) => {
    if (!video) return;
    hydratePoster(video);
    if (video.dataset.gm3Hydrated !== 'true') {
      video.querySelectorAll('source[data-src]').forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
      });
      video.querySelectorAll('img[data-src]').forEach((image) => {
        image.src = image.dataset.src;
        image.removeAttribute('data-src');
      });
      video.dataset.gm3Hydrated = 'true';
      video.load();
    }
    if (shouldPlay && !REDUCED_MOTION.matches && !SAVE_DATA && isVisibleLayout(video)) {
      video.play().catch(() => {});
    }
  };

  const loadScript = (source) => {
    if (!source) return Promise.resolve();
    if (scriptPromises.has(source)) return scriptPromises.get(source);
    const promise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = source;
      script.async = false;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
    scriptPromises.set(source, promise);
    return promise;
  };

  const hydrateScripts = (root) => {
    if (!root || root.dataset.gm3ScriptsHydrated === 'true') return;
    root.dataset.gm3ScriptsHydrated = 'true';
    const scripts = Array.from(root.querySelectorAll('script[data-gm3-lazy-script]'))
      .filter((script) => !(REDUCED_MOTION.matches && script.dataset.gm3MotionOnly === 'true'));
    scripts.reduce((chain, script) => chain.then(() => loadScript(script.dataset.src)), Promise.resolve())
      .catch(() => { root.dataset.gm3ScriptsHydrated = 'error'; });
  };

  const syncVisibleVideos = (scope = document) => {
    scope.querySelectorAll('[data-gm3-reference-story] video').forEach((video) => {
      if (!isVisibleLayout(video)) {
        if (!video.paused) video.pause();
        return;
      }
      const rect = video.getBoundingClientRect();
      const nearViewport = rect.bottom >= -300 && rect.top <= window.innerHeight + 300;
      if (!nearViewport) return;
      if (SAVE_DATA || REDUCED_MOTION.matches) hydratePoster(video);
      else hydrateVideo(video, true);
    });
  };

  const initialise = (scope = document) => {
    const story = scope.matches?.('[data-gm3-reference-story]')
      ? scope
      : scope.querySelector?.('[data-gm3-reference-story]');
    if (!story || story.dataset.gm3PerformanceReady === 'true') return;
    story.dataset.gm3PerformanceReady = 'true';

    const videos = Array.from(story.querySelectorAll('video'));
    const videoObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        if (entry.isIntersecting && isVisibleLayout(video)) {
          if (SAVE_DATA || REDUCED_MOTION.matches) hydratePoster(video);
          else hydrateVideo(video, true);
        } else if (!entry.isIntersecting && !video.paused) {
          video.pause();
        }
      });
    }, { rootMargin: '400px 0px', threshold: 0.01 });
    videos.forEach((video) => videoObserver.observe(video));

    const scriptObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        hydrateScripts(entry.target);
        scriptObserver.unobserve(entry.target);
      });
    }, { rootMargin: '600px 0px', threshold: 0.01 });
    story.querySelectorAll('[data-gm3-lazy-root]').forEach((root) => scriptObserver.observe(root));

    const mutationObserver = new MutationObserver(() => requestAnimationFrame(() => syncVisibleVideos(story)));
    mutationObserver.observe(story, { subtree: true, attributes: true, attributeFilter: ['class', 'hidden', 'aria-selected'] });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) videos.forEach((video) => video.pause());
      else syncVisibleVideos(story);
    });
    window.addEventListener('resize', () => syncVisibleVideos(story), { passive: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialise());
  else initialise();
  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
